import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { extractApiErrorMessage } from '../../../../Utils/apiErrorUtils';
import { OfferLookupEntityOption, OfferLookupField } from '../../Utils/offerLookupAddNew';
import {
  useCreateOfferLookupCountryMutation,
  useCreateOfferLookupHotelMutation,
  useCreateOfferLookupRegionMutation,
} from '../../Service/offersLocationLookupApi';

type AddOfferLookupFormState = {
  name: string;
  description: string;
  published: boolean;
  countryId: number | '';
  shortDescription: string;
  street: string;
  city: string;
  postalCode: string;
};

type CreatedOfferLookupPayload = {
  field: OfferLookupField;
  id: number;
  label: string;
  relatedCountryId?: number;
};

interface AddOfferLookupModalProps {
  open: boolean;
  field: OfferLookupField;
  initialName?: string;
  defaultCountryId?: number;
  countries: OfferLookupEntityOption[];
  onClose: () => void;
  onCreated: (payload: CreatedOfferLookupPayload) => void;
}

const AddOfferLookupModal = ({
  open,
  field,
  initialName,
  defaultCountryId,
  countries,
  onClose,
  onCreated,
}: AddOfferLookupModalProps) => {
  const [createCountry, createCountryState] = useCreateOfferLookupCountryMutation();
  const [createRegion, createRegionState] = useCreateOfferLookupRegionMutation();
  const [createHotel, createHotelState] = useCreateOfferLookupHotelMutation();
  const [formState, setFormState] = useState<AddOfferLookupFormState>(
    getInitialState(field, initialName, defaultCountryId)
  );
  const [submitError, setSubmitError] = useState<string | undefined>();

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormState(getInitialState(field, initialName, defaultCountryId));
    setSubmitError(undefined);
  }, [open, field, initialName, defaultCountryId]);

  const isSubmitting =
    createCountryState.isLoading || createRegionState.isLoading || createHotelState.isLoading;

  const title = useMemo(() => {
    if (field === 'countryId') {
      return 'Dodaj nowy kraj';
    }
    if (field === 'regionId') {
      return 'Dodaj nowy region';
    }
    return 'Dodaj nowy hotel';
  }, [field]);

  const handleInputChange =
    (name: keyof AddOfferLookupFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setSubmitError(undefined);
      const nextValue =
        name === 'published'
          ? event.target.checked
          : name === 'countryId'
          ? event.target.value === ''
            ? ''
            : Number(event.target.value)
          : (event.target.value as string | number);
      setFormState((prev) => ({
        ...prev,
        [name]: nextValue,
      }));
    };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(undefined);

    const validationError = validateForm(field, formState);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    try {
      if (field === 'countryId') {
        const response = await createCountry({
          name: formState.name.trim(),
          description: formState.description.trim(),
          published: formState.published,
        });

        onCreated({
          field,
          id: response.id,
          label: formState.name.trim(),
        });
        return;
      }

      if (field === 'regionId') {
        const response = await createRegion({
          name: formState.name.trim(),
          description: formState.description.trim(),
          countryId: Number(formState.countryId),
        });

        onCreated({
          field,
          id: response.id,
          label: formState.name.trim(),
          relatedCountryId: Number(formState.countryId),
        });
        return;
      }

      const response = await createHotel({
        name: formState.name.trim(),
        shortDescription: formState.shortDescription.trim(),
        description: formState.description.trim(),
        location: '',
        amenities: '',
        accommodation: '',
        meals: '',
        metaTitle: '',
        metaDescription: '',
        metaKeywords: '',
        published: false,
        displayOrder: 0,
        street: formState.street.trim(),
        city: formState.city.trim(),
        postalCode: formState.postalCode.trim(),
      });

      onCreated({
        field,
        id: response.id,
        label: `${formState.name.trim()} • ${formState.city.trim()}`,
      });
    } catch (error) {
      setSubmitError(extractApiErrorMessage(error, 'Nie udało się zapisać danych.'));
    }
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              required
              label="Nazwa"
              value={formState.name}
              onChange={handleInputChange('name')}
              fullWidth
            />

            {(field === 'countryId' || field === 'regionId' || field === 'hotelId') && (
              <TextField
                required
                label="Opis"
                value={formState.description}
                onChange={handleInputChange('description')}
                multiline
                minRows={3}
                fullWidth
              />
            )}

            {field === 'countryId' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formState.published}
                    onChange={handleInputChange('published')}
                  />
                }
                label="Opublikowany"
              />
            )}

            {field === 'regionId' && (
              <TextField
                required
                select
                label="Kraj"
                value={formState.countryId}
                onChange={handleInputChange('countryId')}
                fullWidth
              >
                <MenuItem value="" disabled>
                  Wybierz kraj...
                </MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country.value} value={country.value}>
                    {country.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {field === 'hotelId' && (
              <>
                <TextField
                  required
                  label="Krótki opis"
                  value={formState.shortDescription}
                  onChange={handleInputChange('shortDescription')}
                  fullWidth
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    required
                    label="Ulica"
                    value={formState.street}
                    onChange={handleInputChange('street')}
                    fullWidth
                  />
                  <TextField
                    required
                    label="Miasto"
                    value={formState.city}
                    onChange={handleInputChange('city')}
                    fullWidth
                  />
                </Stack>
                <TextField
                  required
                  label="Kod pocztowy"
                  value={formState.postalCode}
                  onChange={handleInputChange('postalCode')}
                  placeholder="12-345"
                  fullWidth
                />
              </>
            )}
            {submitError && <Alert severity="error">{submitError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Anuluj
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
          >
            Zapisz
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddOfferLookupModal;

function getInitialState(
  field: OfferLookupField,
  initialName?: string,
  defaultCountryId?: number
): AddOfferLookupFormState {
  return {
    name: initialName?.trim() ?? '',
    description: '',
    published: true,
    countryId: field === 'regionId' && isPositiveInt(defaultCountryId) ? defaultCountryId : '',
    shortDescription: '',
    street: '',
    city: '',
    postalCode: '',
  };
}

function validateForm(field: OfferLookupField, values: AddOfferLookupFormState): string | undefined {
  const trimmedName = values.name.trim();
  const trimmedDescription = values.description.trim();

  if (!trimmedName) {
    return 'Nazwa jest wymagana.';
  }

  if (field === 'countryId' || field === 'regionId' || field === 'hotelId') {
    if (!trimmedDescription) {
      return 'Opis jest wymagany.';
    }
  }

  if (field === 'regionId' && !isPositiveInt(values.countryId)) {
    return 'Wybierz kraj dla nowego regionu.';
  }

  if (field === 'hotelId') {
    if (!values.shortDescription.trim()) {
      return 'Krótki opis jest wymagany.';
    }
    if (!values.street.trim()) {
      return 'Ulica jest wymagana.';
    }
    if (!values.city.trim()) {
      return 'Miasto jest wymagane.';
    }
    if (!values.postalCode.trim()) {
      return 'Kod pocztowy jest wymagany.';
    }
    if (!/^\d{2}-\d{3}$/.test(values.postalCode.trim())) {
      return 'Kod pocztowy musi być w formacie 12-345.';
    }
  }

  return undefined;
}

function isPositiveInt(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0;
}
